import { FC } from 'react';
import Checkbox from '@/components/ui/Checkbox/Checkbox.tsx';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import Modal from '@/components/ui/Modal/Modal.tsx';
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import styles from './TripEmissionLog.module.scss';

interface ResolveSelectedTripsModalProps {
	name: string;
	selectedTripsCount: number;
	isPending: boolean;
	isRefreshing: boolean;
	isConfirmed: boolean;
	onConfirmationChange: (isConfirmed: boolean) => void;
	onClose: () => void;
	onResolve: () => void;
}

const ResolveSelectedTripsModal: FC<ResolveSelectedTripsModalProps> = ({
	name,
	selectedTripsCount,
	isPending,
	isRefreshing,
	isConfirmed,
	onConfirmationChange,
	onClose,
	onResolve,
}) => {
	const selectedTripsLabel = selectedTripsCount === 1 ? 'trip' : 'trips';
	const selectedTripsReference = selectedTripsCount === 1 ? 'this trip' : 'these trips';

	return (
		<Modal
			name={name}
			className={styles.resolveSelectedTripsModal}
			closeable={!isPending}
			onClose={() => onConfirmationChange(false)}
			outsideClickEnabled={!isPending}
		>
			{isPending && <Spinner absolute overlay className={styles.modalSpinner} />}
			<h2>
				Resolve missing trip details for {selectedTripsCount} {selectedTripsLabel}?
			</h2>
			<p>Use this option only if accommodation or transportation was not needed for {selectedTripsReference}.</p>
			<p>
				{selectedTripsCount === 1 ? 'This trip' : 'These trips'} will be removed from this table. No emissions
				will be added for the missing accommodation or transportation, which may affect your organization’s CO₂
				reporting.
			</p>
			<Checkbox
				className={styles.resolveSelectedTripsConfirmation}
				isSelected={isConfirmed}
				isDisabled={isPending || isRefreshing}
				onChange={onConfirmationChange}
			>
				I confirm that the missing accommodation or transportation was not needed for {selectedTripsReference}.
			</Checkbox>
			<FlexBlock className={styles.resolveSelectedTripsActions} justifyContent="flex-end" columnGap="12px">
				<button
					type="button"
					className={styles.cancelSelectedTripsButton}
					onClick={onClose}
					disabled={isPending || isRefreshing}
				>
					Back
				</button>
				<button
					type="button"
					className={styles.confirmSelectedTripsButton}
					onClick={onResolve}
					disabled={!selectedTripsCount || !isConfirmed || isPending || isRefreshing}
				>
					Resolve without details
				</button>
			</FlexBlock>
		</Modal>
	);
};

export default ResolveSelectedTripsModal;
